import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Set as FestivalSet } from '../../../types'
import { getNow } from '../../../lib/clock'
import {
  DEFAULT_GRID_GEOMETRY,
  computeNowLineMinutes,
  festivalMinutesToTop,
  measureGridGeometry,
} from './timeUtils'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'

/** The day grid's scroller/grid refs, the initial scroll-to-now, and the ?set= scroll + flash. */
export function useDayGridScroll({
  day,
  sets,
  gridMin,
  gridMax,
  hasSets,
  timezone,
  highlightSetId,
  onHighlightConsumed,
}: {
  day: string
  sets: FestivalSet[]
  gridMin: number
  gridMax: number
  hasSets: boolean
  timezone?: string
  highlightSetId?: string | null
  onHighlightConsumed?: () => void
}) {
  const cutoff = useDayCutoff()
  const scrollerRef = useRef<HTMLDivElement>(null)
  // The clock-driven now-line owns this ref via <NowLine>; DayGrid itself no
  // longer subscribes to the clock, so ticks don't re-render the grid.
  const gridRef = useRef<HTMLDivElement>(null)
  const blockRefs = useRef(new Map<string, HTMLDivElement>())
  // Stable so React.memo(SetBlock) isn't defeated by a fresh ref callback each render.
  const registerBlock = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) blockRefs.current.set(id, el)
    else blockRefs.current.delete(id)
  }, [])
  const [flashSetId, setFlashSetId] = useState<string | null>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Navigated here via ?set=<id>: scroll the block into the center of the
  // scroller (both axes — it may be in an off-screen stage column) and pulse a
  // ring on it. Wait until the set is actually rendered (sets load async), then
  // consume the URL signal so it fires exactly once.
  useEffect(() => {
    if (!highlightSetId) return
    if (!sets.some(s => s.id === highlightSetId)) return
    const el = blockRefs.current.get(highlightSetId)
    const scroller = scrollerRef.current
    if (!el || !scroller) return
    scroller.scrollTo({
      top: Math.max(0, el.offsetTop - scroller.clientHeight / 2 + el.offsetHeight / 2),
      left: Math.max(0, el.offsetLeft - scroller.clientWidth / 2 + el.offsetWidth / 2),
      behavior: 'smooth',
    })
    setFlashSetId(highlightSetId)
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setFlashSetId(null), 3400)
    onHighlightConsumed?.()
  }, [highlightSetId, sets, onHighlightConsumed])

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
  }, [])

  // Auto-scroll once on mount. If "now" falls within this day's range, land
  // the now-line ~33% down the visible area (more upcoming sets below).
  // Otherwise scroll to the bottom so headliners are visible first. Reads the
  // clock directly (one-shot) so the grid needn't subscribe to per-minute ticks.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    // A pending ?set= highlight owns the initial scroll position — don't fight it.
    if (highlightSetId) return
    const max = scroller.scrollHeight - scroller.clientHeight
    const nowMin = computeNowLineMinutes(getNow(), day, gridMin, gridMax, hasSets, cutoff, timezone)
    if (nowMin === null) {
      scroller.scrollTop = max
      return
    }
    const { slotPx, headerPx } = measureGridGeometry(gridRef.current) ?? DEFAULT_GRID_GEOMETRY
    const target = headerPx + festivalMinutesToTop(nowMin, gridMin, slotPx) - scroller.clientHeight * 0.33
    scroller.scrollTop = Math.max(0, Math.min(max, target))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrolls to "now" once on mount only
  }, [])

  return { scrollerRef, gridRef, blockRefs, registerBlock, flashSetId }
}
